import React from 'react';
import { ActionIcon, Card, Text, useMantineTheme, Menu, Group, SimpleGrid, Badge, Box } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';

import { utils } from "../../utils/globalVariables";
import { numberFormatter, sqlDateToHumanDate, getObjectParam2, safeValue, stringDivider } from "../../utils/globalFunctions";

import LabelContentInline from "../../components/general/LabelContentInline";
import SortSegmentedControl from "../../components/general/SortSegmentedControl";

const StockItemCard = ({
  stateData,
  stateSortList,
  stateSortSelected,
  fnSort,
  fnSelectedDataEdit,
  fnSelectedDataTagging,
  fnSelectedDataDelete,
  fnSelectedDataView,
  fnSelectedInStock,
  fnSelectedKlaimGaransi
}) => {

  const theme = useMantineTheme();

  return (
    <React.Fragment>

      <SortSegmentedControl
        stateSortList={stateSortList}
        stateSortSelected={stateSortSelected}
        fnSort={fnSort}
        stateStyle={{ marginBottom: theme.spacing.md }}
      />

      <SimpleGrid
        cols={1}
        spacing='md'
        breakpoints={[
          { minWidth: 'xl', cols: 3, spacing: 'md' },
          { minWidth: 'lg', cols: 2, spacing: 'md' },
          { minWidth: 'md', cols: 2, spacing: 'md' },
          { minWidth: 'sm', cols: 2, spacing: 'md' },
          { minWidth: 'xs', cols: 2, spacing: 'md' },
        ]}
      >
        {Object.keys(stateData).map((key, index) => {
          return (
            <Card
              key={key}
              shadow=''
              radius="md"
              withBorder={true}

              style={{
                borderColor: theme.colorScheme === 'light' ? theme.colors.gray[2] : theme.colors.dark[7],
                backgroundColor: theme.colorScheme === 'light' ? '#fff' : theme.colors.dark[7],
                overflow: 'unset',
                cursor: 'pointer',
              }}

              onClick={() => {
                fnSelectedDataView(stateData[key]);
              }}
            >
              <Card.Section p='sm'>
                <Text size="sm">
                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockCategory.caption}
                    stateContent={
                      <Group position='apart' noWrap align='flex-start'>
                        <Box>
                          <Text lineClamp={2}>{stateData[key].stock_category_name}</Text>
                        </Box>

                        <Menu shadow="md" width={150}>
                          <Menu.Target>
                            <ActionIcon
                              size='md'
                              variant="subtle"
                              radius='xl'

                              sx={{
                                '&:hover': {
                                  backgroundColor: theme.colorScheme === 'light' ? theme.colors.gray[1] : theme.colors.dark[5]
                                },
                              }}

                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <IconDotsVertical size='1rem' />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            <Menu.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                fnSelectedDataTagging(stateData[key]);
                              }}
                            >
                              Tagging
                            </Menu.Item>

                            <Menu.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                fnSelectedInStock(stateData[key])
                              }}
                            >
                              Stok Rusak
                            </Menu.Item>

                            <Menu.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                fnSelectedKlaimGaransi(stateData[key])
                              }}
                            >
                              Klaim Garansi
                            </Menu.Item>

                            <Menu.Divider />

                            <Menu.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                fnSelectedDataEdit(stateData[key]);
                              }}
                            >
                              Edit Data
                            </Menu.Item>

                            <Menu.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                fnSelectedDataDelete(stateData[key])
                              }}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    }
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockCategorySub.caption}
                    stateContent={<Text lineClamp={2}>{stateData[key].stock_category_sub_name}</Text>}
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel='Serial Number'
                    stateContent={
                      <Text
                        sx={{
                          wordBreak: 'break-word'
                        }}
                      >
                        {stateData[key].stock_serial_number ? stateData[key].stock_serial_number : ' - '}
                      </Text>
                    }
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockSupplier.caption}
                    stateContent={<Text lineClamp={2}>{stateData[key].supplier_name}</Text>}
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockTanggalBeli.caption}
                    stateContent={sqlDateToHumanDate({
                      stateDate: stateData[key].stock_tanggal_beli,
                      stateWithTime: false,
                      stateLang: 'id',
                      stateIsShort: false,
                      stateDay: false,
                      stateDefaultValue: ' - ',
                      stateDateType: 'day'
                    })}
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockHargaBeli.caption}
                    stateContent={'Rp ' + numberFormatter({ stateValue: stateData[key].stock_harga_beli })}
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockHargaJual.caption}
                    stateContent={'Rp ' + numberFormatter({ stateValue: stateData[key].stock_harga_jual })}
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockMasaGaransiType.caption}
                    stateContent={
                      <Box>
                        <Text>
                          {getObjectParam2(utils.masaGaransi, stateData[key].stock_masa_garansi_type, 'caption', '-')}
                        </Text>

                        <Text>
                          <Text>
                            {safeValue(stateData[key].stock_masa_garansi_bulan, '0') + ' Bulan'}
                          </Text>
                          <Text>
                            {safeValue(stateData[key].stock_masa_garansi_hari, '0') + ' Hari'}
                          </Text>
                        </Text>
                      </Box>
                    }
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockJumlah.caption}
                    stateContent={
                      <Box>
                        <Group>
                          <Text>
                            {numberFormatter({ stateValue: stateData[key].stock_jumlah })}
                          </Text>

                          <Badge
                            size='md'
                            color={getObjectParam2(utils.stockItemStatus, stateData[key].stock_status, 'color', 'gray')}
                          >
                            {getObjectParam2(utils.stockItemStatus, stateData[key].stock_status, 'caption', ' - ')}
                          </Badge>
                        </Group>

                        {/* bergamore - show tagged */}
                        {stateData[key].customer_name &&
                          <Text
                            size='xs'
                            color={getObjectParam2(utils.stockItemStatus, stateData[key].stock_status, 'color', 'gray')}
                            mt='sm'
                          >
                            Tagged {safeValue(stateData[key].customer_name, ' - ')}
                          </Text>
                        }

                        {stateData[key].tagged_project_id &&
                          <Text
                            size='xs'
                            color={getObjectParam2(utils.stockItemStatus, stateData[key].stock_status, 'color', 'gray')}
                            mt='sm'
                          >
                            Tagged {stringDivider(stateData[key].tagged_project_id, [4, 6, 8], '.', ' - ')}
                          </Text>
                        }
                      </Box>
                    }
                    stateDivider={true}
                  />

                  <LabelContentInline
                    stateColLabel={4}
                    stateLabel={stateSortList.stockIsActive.caption}
                    stateContent={stateData[key].stock_is_active === '1' ? 'Aktif' : 'Non Aktif'}
                    stateDivider={false}
                  />
                </Text>
              </Card.Section>
            </Card>
          )
        })}
      </SimpleGrid>
    </React.Fragment>
  )
}

export default React.memo(StockItemCard);